import React from "react";
import { Card, CardBody, Spinner, Button } from "reactstrap";
import { resolveMediaUrl, calculateMemberCount } from "./utils";
import { formatLastMessageTime } from "./utils";

const GroupChatList = ({
  groupChats,
  loading,
  groupListError,
  currentChat,
  selectedChatId,
  currentUserId,
  unread,
  typingUsers,
  handleSelectGroup,
  groupsListRef,
  dispatch,
  clearChatError,
  fetchAllGroupChats,
  hasMoreGroups
}) => {
  const getDisplayMessage = (chat) => {
    if (!chat.lastMessage) {
      return "No messages yet";
    }

    // Check if message is deleted
    if (chat.lastMessage.isDeleted || chat.lastMessage.isDeleteEvery) {
      return "This message was deleted";
    }

    // Handle different message types
    if (chat.lastMessage.messageType === "image") {
      return "📷 Photo";
    } else if (chat.lastMessage.messageType === "video") {
      return "🎥 Video";
    } else if (chat.lastMessage.messageType === "file") {
      return "📎 File";
    }

    // Text message - truncate if too long
    const content = chat.lastMessage.content || "Message";
    return content.length > 25 ? content.substring(0, 25) + '...' : content;
  };

  const getSenderName = (chat) => {
    if (!chat.lastMessage || !chat.lastMessage.sender) {
      return "";
    }

    const sender = chat.lastMessage.sender;

    // Check if current user is the sender
    if (sender._id === currentUserId) {
      return "You: ";
    }

    // Return sender's first name
    return `${sender.firstname || "User"}: `;
  };

  // ✅ FIXED: Simplified badge count - just use the unreadCount from Redux state
  const getBadgeCount = (chat) => {
    const badgeCount = chat.unreadCount || 0;

    console.log("🔢 [GroupChatList] Badge count for chat:", {
      chatId: chat._id || chat.chatRequestId,
      chatName: chat.name,
      unreadCount: badgeCount,
      timestamp: new Date().toISOString()
    });

    return badgeCount;
  };

  return (
    <Card className="shadow-sm h-100" style={{ overflow: "auto" }} ref={groupsListRef}>
      <CardBody style={{ padding: 0 }}>
        <div className="flex space-between" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h4 className="card-title mb-0" style={{ position: "sticky", top: 0, padding: "1rem", background: "white", zIndex: 10 }}>Group Chats</h4>
          <h6 className="mb-0" style={{ position: "sticky", top: 0, padding: "1rem", background: "white", zIndex: 10, color: "#4950578f" }}>
            Total Groups: {groupChats.length}
          </h6>
        </div>

        {loading && groupChats.length === 0 && (
          <div className="text-center py-4">
            <Spinner size="sm" color="primary" /> Loading...
          </div>
        )}

        {groupListError && (
          <div className="text-center py-2">
            <p className="text-danger mb-2">{groupListError}</p>
            <Button
              size="sm"
              color="primary"
              onClick={() => {
                dispatch(clearChatError());
                dispatch(fetchAllGroupChats({ page: 1, limit: 50 }));
              }}
            >
              Retry
            </Button>
          </div>
        )}

        {!loading && !groupListError && groupChats.length === 0 && (
          <p className="text-muted text-center">No group chats available.</p>
        )}

        <ul className="list-unstyled mb-0" style={{ padding: "0rem 1rem 1rem 1rem" }}>
          {groupChats.map((chat, index) => {
            const totalMembers = calculateMemberCount(chat);

            // ✅ FIXED: Simplified badge count
            const badgeCount = getBadgeCount(chat);

            const cid = chat._id || chat.chatRequestId || chat.groupId;
            const same = (a, b) => a && b && String(a) === String(b);
            const isSelected = (
              same(currentChat?._id, chat._id) ||
              same(currentChat?.chatRequestId, chat.chatRequestId) ||
              same(currentChat?.groupId, chat.groupId) ||
              same(selectedChatId, chat._id) ||
              same(selectedChatId, chat.chatRequestId)
            );

            const allTypingUsers = typingUsers[String(cid)] || [];
            const isCurrentUserTyping = allTypingUsers.some((id) => String(id) === String(currentUserId));
            const typingInThisChat = allTypingUsers.filter((id) => String(id) !== String(currentUserId));

            return (
              <li
                key={chat._id || chat.chatRequestId || `chat-${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelectGroup(chat);
                }}
                className={`d-flex align-items-center justify-content-between p-2 mb-2 ${isSelected ? "border border-2 border-primary" : "bg-light-subtle"}`}
                style={{ cursor: "pointer", borderRadius: "6px", position: "relative" }}
              >
                <div className="d-flex align-items-center flex-grow-1">
                  {chat.groupImage ? (
                    <img
                      src={resolveMediaUrl(chat.groupImage)}
                      alt={chat.name || "Group"}
                      className="rounded-circle me-3"
                      style={{ width: 40, height: 40, objectFit: "cover" }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        const fallback = e.target.parentElement.querySelector('.group-avatar-fallback');
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                  ) : <div
                    className="avatar-xs rounded-circle bg-primary-subtle text-primary fw-bold d-flex align-items-center justify-content-center me-3 group-avatar-fallback"
                    style={{ width: 40, height: 40, display: chat.groupImage ? "none" : "flex" }}
                  >
                    {chat.name?.charAt(0)?.toUpperCase() || "G"}
                  </div>
                  }

                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    {/* Group name and time in flex container */}
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <h6 className={`mb-0 text-truncate ${badgeCount > 0 ? "text-primary fw-bold" : "text-dark"}`} style={{ maxWidth: "150px" }}>
                        {chat.name || "Group"}
                      </h6>

                      {/* Last message time */}
                      {chat.lastMessageTimestamp && (
                        <small className="text-muted text-nowrap ms-2">
                          {formatLastMessageTime(chat.lastMessageTimestamp)}
                        </small>
                      )}
                    </div>

                    {isCurrentUserTyping || typingInThisChat.length >= 5 ? (
                      <small className="text-primary fst-italic typing-indicator-text">
                        typing...
                      </small>
                    ) : (
                      <>
                        {typingInThisChat.length === 0 ? (
                          <>
                            {/* Show last message if available, otherwise show member count */}
                            {chat.lastMessage && chat.lastMessage.content ? (
                              <small className="text-muted last-message-text text-truncate d-block">
                                <span className="text-muted">
                                  {getSenderName(chat)}
                                </span>
                                {getDisplayMessage(chat)}
                              </small>
                            ) : (
                              <small className="text-muted member-count-text">
                                {totalMembers} member{totalMembers !== 1 ? "s" : ""}
                              </small>
                            )}
                          </>
                        ) : (
                          <small className="text-primary fst-italic d-block typing-indicator-text">
                            typing...
                          </small>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {badgeCount > 0 && (
                  <span
                    className="badge bg-primary text-white rounded-circle d-flex align-items-center justify-content-center ms-2"
                    style={{
                      minWidth: "20px",
                      height: "20px",
                      fontSize: "0.5rem",
                      padding: "0px",
                      flexShrink: 0,
                      position: "absolute",
                      bottom: "7px",
                      right: "10px",
                    }}
                  >
                    {badgeCount > 9 ? "9+" : badgeCount}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {/* Loading indicator when scrolling for more groups */}
        {loading && groupChats.length > 0 && hasMoreGroups && (
          <div className="text-center py-3">
            <Spinner size="sm" color="primary" /> Loading more groups...
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default GroupChatList;